import { Text } from "@solomon-apps/shared/src/components/ui/text";
import { LightBarActionButton } from "@solomon-apps/shared/src/components/Yaas/LightBarActionButton";
import {
    ArrowRightLongIcon,
    RetryIcon
} from "@solomon-labs/design/components/icons";
import React, { useEffect, useState } from "react";

import { isAlreadyRegisteredCopy } from "../errorCopy";
import type { SubmitSlice } from "../steps";
import {
    resolveSubmitButton,
    SUBMIT_BUTTON_RESERVE_LABELS
} from "../submitButtonState";

/* ── Step 3 — Submit ────────────────────────────────────────────────── */

/**
 * The final step's submit action — a raised light-bar button mirroring the
 * Verify button's status pattern. Clicking calls the real create-position
 * mutation: idle → submitting ("Adding...", or "Submitting" for manual
 * review; pending bar) → submitted ("Added" / "Submitted"; success bar), no
 * message beside it, then after a 3s beat calls `onSubmitted()`
 * so the page drops the empty-state override and the normal positions table
 * renders. No path shows a message line: the action carries every outcome.
 * A failed submit reads Submit Failed, then Retry; an address registered
 * meanwhile, Already Registered, then Add New Address; an expired, consumed
 * or destination-mismatched proof, Verification Expired / Submit Failed /
 * Verification Expired, then Verify Again, which re-verifies in place
 * (`submit.reverify`; the label logic is `resolveSubmitButton`). The review receipt itself is `ReviewGroup`, rendered in the measured stack;
 * this bar stays anchored to the frame bottom.
 */
export function SubmitGroup({
    submit,
    onSubmitted
}: {
    submit: SubmitSlice;
    /** Called 3s after a successful submit — hands control back to the host. */
    onSubmitted: () => void;
}): React.ReactElement {
    const { onSubmit, isSubmitting, error: submitError, submitted } = submit;
    const failed = submitError !== null || Boolean(submit.reverify);

    // On a failure the button holds its failure label for 3s, then becomes
    // the recovery action (Retry, Verify Again, Add New Address), the verify
    // button's pattern. No message line: `reverify` alone starts the hold for
    // a proof that needs replacing.
    const [retryReady, setRetryReady] = useState(false);
    useEffect(() => {
        if (!failed) {
            setRetryReady(false);
            return;
        }
        setRetryReady(false);
        const timer = window.setTimeout(() => setRetryReady(true), 3000);
        return (): void => window.clearTimeout(timer);
    }, [failed]);

    // Once submitted, wait 3s then hand control back to the page (clears the
    // empty-state override → the positions table renders).
    useEffect(() => {
        if (
            !submitted ||
            submit.pendingReview ||
            submit.registeredPositionCount === 0
        )
            return;
        const timer = window.setTimeout(() => onSubmitted(), 3000);
        return (): void => window.clearTimeout(timer);
    }, [
        submitted,
        submit.pendingReview,
        submit.registeredPositionCount,
        onSubmitted
    ]);

    // Registered by someone else after the entry-time check: the action says
    // it (Already Registered, then Add New Address), so no error line.
    const alreadyRegistered = isAlreadyRegisteredCopy(submitError);
    const button = resolveSubmitButton({
        isSubmitting,
        submitted,
        failed,
        holdDone: retryReady,
        automaticEnrollment: submit.automaticEnrollment ?? false,
        reverify: submit.reverify ?? null,
        alreadyRegistered,
        claimRequired: submit.claimRequired
    });

    const handleClick = (): void => {
        if (button.action === "submit") {
            if (submit.claimRequired) submit.onConfirmClaim?.();
            else onSubmit();
        } else if (button.action === "verify") submit.reverify?.onVerifyAgain();
        else if (button.action === "restart") submit.onStartOver?.();
    };

    return (
        <div className="flex flex-col items-start gap-4">
            {submit.claimRequired && !submitted && (
                <div
                    role="note"
                    className="border-l-2 border-line-base pl-3 text-foreground-muted"
                >
                    <Text variant="fine-print">
                        Claim this retail wallet and all its positions under
                        this issuer for your company.
                    </Text>
                    <Text variant="fine-print">
                        All claimed and new positions use this destination.
                    </Text>
                </div>
            )}
            {!submit.claimRequired && !submitted && (
                <Text variant="small">
                    This destination is the default for new positions. Each
                    program applies its own eligibility rules.
                </Text>
            )}
            {submitted && submit.registeredPositionCount === 0 && (
                <div role="status">
                    <Text variant="small">
                        Wallet registered. No program positions are available
                        yet. Eligible programs will create positions
                        automatically.
                    </Text>
                    <button
                        type="button"
                        className="underline"
                        onClick={onSubmitted}
                    >
                        <Text variant="small">Back to Positions</Text>
                    </button>
                </div>
            )}
            <LightBarActionButton
                label={button.label}
                size="lg"
                status={button.status}
                disabled={button.disabled}
                onClick={handleClick}
                reserveLabels={SUBMIT_BUTTON_RESERVE_LABELS}
                icon={
                    button.retryIcon ? (
                        <RetryIcon className="h-4 w-4" />
                    ) : button.arrowIcon ? (
                        <ArrowRightLongIcon className="size-5" />
                    ) : undefined
                }
            />
            {submit.claimRequired && !submitted && !isSubmitting && (
                <button
                    type="button"
                    className="text-sm underline"
                    onClick={submit.onStartOver}
                >
                    Cancel
                </button>
            )}
        </div>
    );
}
